import { createClient, createConfig } from "@hey-api/client-fetch";

import { isAppToken } from "../utils/appToken";
import { getAccessTokenCookie } from "../utils/cookies";
import { getEnv } from "../utils/env";
import { authDebug, authDebugError } from "../utils/safeAuthLog";
import { logoutUser, refreshTokenIfNeeded } from "../utils/tokenManager";

// Create a custom createClient function that adds auth token to all requests
export const createApiClient = () => {
  return createClient(
    createConfig({
      baseUrl: getEnv("VITE_BACKEND_URL") || "http://localhost:8000",
      fetch: async (request: Request) => {
        // Store original request details for potential retry
        const originalUrl = request.url;
        const originalMethod = request.method;
        const originalHeaders = new Headers(request.headers);

        // Read body before first request (needed for retry on 401)
        // For GET/HEAD requests, body is null
        let originalBody: BodyInit | null = null;
        if (request.body && originalMethod !== "GET" && originalMethod !== "HEAD") {
          try {
            // Clone to safely read the body without consuming original
            const clonedRequest = request.clone();
            originalBody = await clonedRequest.arrayBuffer();
          } catch (error) {
            // Avoid logging request bodies / error objects in production (may contain sensitive data)
            authDebugError(
              "apiClient: could not read request body for retry",
              error
            );
            // If we can't read body, we can't retry POST/PUT/PATCH requests
            // But we'll still try the initial request
          }
        }

        // Get the access token from in-memory store (not from cookie; avoids XSS)
        const accessToken = getAccessTokenCookie();

        // Add the token to the Authorization header if it exists
        if (accessToken) {
          originalHeaders.set("Authorization", `Bearer ${accessToken}`);
        }

        // Create the request with auth header. `credentials: "include"` lets the
        // OTP browser-session cookie (MDRS-241 #6, HttpOnly, path-scoped to
        // /api/v1/auth) travel even cross-origin in dev; same-origin behind Front
        // Door it would flow anyway. Bearer token stays the primary auth.
        const requestInit: RequestInit = {
          method: originalMethod,
          headers: originalHeaders,
          body: originalBody,
          signal: request.signal,
          credentials: "include",
        };

        // Use the native fetch with our modified request
        let response = await fetch(originalUrl, requestInit);

        // Handle 403 USER_BLOCKED — logout immediately, leave other 403s alone
        if (response.status === 403) {
          let isBlocked = false;
          try {
            const body = await response.clone().json();
            isBlocked = body?.detail === "USER_BLOCKED";
          } catch {
            // Non-JSON body — not a block response
          }
          if (isBlocked) {
            await logoutUser(true);
            throw new Error(
              "Your account has been blocked. Please contact support."
            );
          }
        }

        // Handle 401 Unauthorized responses - refresh token and retry
        if (response.status === 401) {
          // External (email-code) users have no MSAL account, so silent refresh
          // can't help — send them straight back to sign in.
          if (isAppToken(accessToken)) {
            authDebug("apiClient: 401 on app token; re-login required");
            await logoutUser(true);
            throw new Error("Your session has expired. Please sign in again.");
          }

          authDebug("apiClient: received 401, attempting token refresh");

          try {
            // Try to refresh the token first
            const refreshSuccess = await refreshTokenIfNeeded();

            if (refreshSuccess) {
              authDebug("apiClient: token refresh successful, retrying request");
              // Get the new token from in-memory store
              const newAccessToken = getAccessTokenCookie();

              if (newAccessToken) {
                // Create a new request with the refreshed token
                const retryHeaders = new Headers(originalHeaders);
                retryHeaders.set("Authorization", `Bearer ${newAccessToken}`);

                const retryRequestInit: RequestInit = {
                  method: originalMethod,
                  headers: retryHeaders,
                  body: originalBody,
                  signal: request.signal,
                  credentials: "include",
                };

                // Retry the request with the new token
                response = await fetch(originalUrl, retryRequestInit);

                // If we still get 401 after refresh, user needs to re-authenticate
                if (response.status === 401) {
                  authDebug("apiClient: still 401 after refresh; logging out");
                  await logoutUser(true);
                  throw new Error("Authentication failed. Please sign in again.");
                }

                return response;
              } else {
                authDebug(
                  "apiClient: no new token available after refresh; logging out"
                );
                await logoutUser(true);
                throw new Error("Authentication failed. Please sign in again.");
              }
            } else {
              authDebug("apiClient: token refresh failed; logging out");
              // Clear authentication state and redirect to login
              await logoutUser(true);
              throw new Error("Authentication failed. Please sign in again.");
            }
          } catch (refreshError) {
            authDebugError("apiClient: token refresh error", refreshError);
            // Clear authentication state and redirect to login
            await logoutUser(true);
            throw new Error("Authentication failed. Please sign in again.");
          }
        }

        return response;
      },
    })
  );
};

// Create an instance of the API client
export const apiClient = createApiClient();
